import React from 'react';
import styled, { css } from 'styled-components';
import { FormattedMessage } from 'react-intl';

import Chip from '../brand/Chip';
import { Section, Inner, SectionTitle, Cover } from './shared';
import { media } from '../../styles/style-utils';
import { type } from '../../styles/typography';
import decor from '../../assets/images/home/usp-decor.svg';
import fairPay from '../../assets/images/home/fair-pay.jpg';

const Wrapper = styled(Section)`
  position: relative;
  padding-top: 60px;
  padding-bottom: 72px;
  overflow: hidden;
  background-color: var(--b-ink);
  color: var(--b-white);

  ${media.large`
    padding-bottom: 108px;
  `};
`;

const Content = styled(Inner)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 48px;

  ${media.large`
    gap: 68px;
  `};
`;

const Decor = styled.img`
  position: absolute;
  top: 80px;
  left: 78%;
  width: 387px;
  height: 260px;
  pointer-events: none;
  display: none;

  ${media.large`
    display: block;
  `};
`;

const Cards = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  ${media.large`
    grid-template-columns: 373fr 665fr 330fr;
    align-items: end;
  `};
`;

const Card = styled.article`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  padding: 32px 20px;
  border-radius: var(--b-radius);
  color: var(--b-black);
  gap: 20px;

  h3 {
    margin: 0;
    padding: 0;
    color: inherit;
  }

  p {
    ${type('body', 'medium')};
    margin: 0;
    color: rgba(0, 0, 0, 0.6);
  }
`;

const TALL = 488;

const EXPAND_EASING = '0.45s cubic-bezier(0.22, 1, 0.36, 1)';

const expandable = restHeight => css`
  justify-content: space-between;
  min-height: ${restHeight}px;
  transition: min-height ${EXPAND_EASING};

  > :last-child {
    transition: margin-bottom ${EXPAND_EASING};
  }
`;

const Discovery = styled(Card)`
  ${expandable(230)};
  background-color: var(--b-lilac);

  > div + div {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  h3 {
    ${type('headline', 'small')};
  }

  ${media.large`
    ${expandable(347)};
  `};
`;

const FairPay = styled(Card)`
  min-height: ${TALL}px;
  padding: 0;
  overflow: hidden;
  background-color: var(--b-white);

  > div {
    display: flex;
    flex-direction: column;
    gap: 32px;
    padding: 32px 20px;
  }

  h3 {
    ${type('headline', 'medium')};
  }

  ${media.large`
    height: ${TALL}px;
  `};

  figure {
    flex: 1 1 auto;
    min-height: 0;
    height: 292px;
    margin: auto 0 0;
    overflow: hidden;
    background-color: var(--b-smoke);

    img {
      object-position: center 25%;
    }
  }
`;

const Lossless = styled(Card)`
  ${expandable(183)};
  background-color: var(--b-sky);

  h3 {
    ${type('headline', 'small')};
  }
`;

const Usps = () => (
  <Wrapper>
    <Content>
      <SectionTitle>
        <FormattedMessage
          id="home.usps.title"
          defaultMessage="Built differently{br}where it matters."
          values={{ br: <br /> }}
        />
      </SectionTitle>
      <Cards>
        <Decor src={decor} alt="" aria-hidden="true" />
        <Discovery>
          <div>
            <Chip tone="translucent" uppercase>
              <FormattedMessage id="home.usps.discovery.chip" defaultMessage="Discovery" />
            </Chip>
          </div>
          <div>
            <h3>
              <FormattedMessage
                id="home.usps.discovery.title"
                defaultMessage="Search by Composer, Ensemble, Soloist and more."
                values={{ br: <br /> }}
              />
            </h3>
            <p>
              <FormattedMessage
                id="home.usps.discovery.text"
                defaultMessage="Explore works and interpretations with search made for classical music."
              />
            </p>
          </div>
        </Discovery>
        <FairPay>
          <div>
            <div>
              <Chip tone="dark" uppercase>
                <FormattedMessage id="home.usps.fair-pay.chip" defaultMessage="Fair-Pay" />
              </Chip>
            </div>
            <h3>
              <FormattedMessage
                id="home.usps.fair-pay.title"
                defaultMessage="Your Subscription Supports{br}the Artists You Listen To."
                values={{ br: <br /> }}
              />
            </h3>
          </div>
          <figure>
            <Cover src={fairPay} alt="" />
          </figure>
        </FairPay>
        <Lossless>
          <div>
            <Chip tone="white" uppercase>
              <FormattedMessage id="home.usps.lossless.chip" defaultMessage="Lossless" />
            </Chip>
          </div>
          <h3>
            <FormattedMessage
              id="home.usps.lossless.title"
              defaultMessage="Lossless Sound.{br}Effortless Listening."
              values={{ br: <br /> }}
            />
          </h3>
        </Lossless>
      </Cards>
    </Content>
  </Wrapper>
);

export default Usps;



// WEBPACK FOOTER //
// /tmp/build_2cb81483/src/components/home/Usps.js